import { useCallback, useEffect, memo } from 'react';
import styled from 'styled-components';
import { GREAT_WHITE } from 'HubStyleTokens/colors';
import PropTypes from 'prop-types';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import UILink from 'UIComponents/link/UILink';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { getResult, getHasRecording, getRecordingUrl, isResultSuccessful, getCRMObjectId, getHasVoicemail } from '../call-message/operators/callMessageGetters';
import CallMessagePropType from '../operators/CallMessagePropType';
import CallMetadataError from './CallMetadataError';
import CallRecordingController from './CallRecordingController';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const StyledDividerLine = styled.div.withConfig({
  displayName: "CallRecording__StyledDividerLine",
  componentId: "sc-16q1ol-0"
})(["border-bottom:1px solid ", ";"], GREAT_WHITE);
function CallRecording({
  callMessage,
  duration,
  handlePostCallPolling,
  resetCallMessage,
  onDeleteRecording,
  timestamp
}) {
  const hasRecording = getHasRecording(callMessage);
  const hasVoicemail = getHasVoicemail(callMessage);
  const recordingUrl = getRecordingUrl(callMessage);
  const engagementId = getCRMObjectId(callMessage);
  const hasRecordingResult = getResult(hasRecording);
  const isRecordingResultSuccessful = isResultSuccessful(hasRecording);
  const recordingResultUrl = hasRecordingResult && getResult(recordingUrl);
  const twoMinutes = 1000 * 60 * 2;
  const isCallOlderThanTwoMinutes = Date.now() - timestamp > twoMinutes;
  const recordingWontExist = hasRecording && !recordingResultUrl && isCallOlderThanTwoMinutes;
  useEffect(() => {
    if (hasRecording && !recordingResultUrl && !isCallOlderThanTwoMinutes) {
      handlePostCallPolling();
    }
  }, [hasRecording, recordingResultUrl, isCallOlderThanTwoMinutes, handlePostCallPolling]);
  const CallRecordingContent = useCallback(() => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(StyledDividerLine, {}), /*#__PURE__*/_jsx("div", {
        className: "p-top-4 p-bottom-3",
        children: recordingResultUrl ? /*#__PURE__*/_jsx("div", {
          "data-selenium-test": "call-recording-content",
          "data-test-id": "call-recording-content",
          children: /*#__PURE__*/_jsx(CallRecordingController, {
            recordingUrl: recordingResultUrl,
            duration: duration,
            engagementId: engagementId,
            onDeleteRecording: onDeleteRecording,
            callMessage: callMessage,
            hasVoicemail: hasVoicemail
          })
        }) : /*#__PURE__*/_jsx(UILoadingSpinner, {
          size: "extra-small",
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: hasVoicemail ? 'conversations-calling-message.timeline-call-metadata.call-voicemail.loading' : 'conversations-calling-message.timeline-call-metadata.call-recording.loading'
          }),
          className: "m-x-0",
          showLabel: true
        })
      })]
    });
  }, [recordingResultUrl, duration, engagementId, callMessage, onDeleteRecording, hasVoicemail]);
  if (!isRecordingResultSuccessful) {
    return /*#__PURE__*/_jsx(CallMetadataError, {
      children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: hasVoicemail ? 'conversations-calling-message.timeline-call-metadata.call-voicemail.error_jsx' : 'conversations-calling-message.timeline-call-metadata.call-recording.error_jsx',
        options: {
          onClick: resetCallMessage
        },
        elements: {
          UILink
        }
      })
    });
  } else if (!hasRecordingResult) {
    return null;
  }
  if (recordingWontExist) {
    return null;
  }
  return /*#__PURE__*/_jsx(CallRecordingContent, {});
}
CallRecording.propTypes = {
  callMessage: CallMessagePropType.isRequired,
  duration: PropTypes.number,
  resetCallMessage: PropTypes.func.isRequired,
  onDeleteRecording: PropTypes.func.isRequired,
  timestamp: PropTypes.number.isRequired
};
export default /*#__PURE__*/memo(CallRecording);